export interface ClientTimeContext {
  timezone: string;
  locale: string;
  timezoneOffsetMinutes: number;
  timezoneSource: "iana" | "offset";
}

const TIMEZONE_CACHE_KEY = "ayesearch_client_timezone";

function readCachedTimezone(): string {
  try {
    return window.localStorage.getItem(TIMEZONE_CACHE_KEY) || "";
  } catch {
    return "";
  }
}

function saveCachedTimezone(timezone: string): void {
  if (!timezone) return;

  try {
    window.localStorage.setItem(TIMEZONE_CACHE_KEY, timezone);
  } catch {
    // Timezone still travels with the current request.
  }
}

function detectIanaTimezone(): string {
  try {
    const timezone =
      Intl.DateTimeFormat().resolvedOptions().timeZone?.trim() || "";

    if (timezone) {
      saveCachedTimezone(timezone);
      return timezone;
    }
  } catch {
    // Fall through to the most recently valid browser timezone.
  }

  return readCachedTimezone();
}

export function getClientTimeContext(): ClientTimeContext {
  const timezone = detectIanaTimezone();

  let locale = "";
  try {
    locale =
      navigator.languages?.[0] ||
      navigator.language ||
      "";
  } catch {
    locale = "";
  }

  // JavaScript convention:
  // PDT => 420, EDT => 240, JST => -540.
  // Backend knows how to convert this to a UTC offset.
  const timezoneOffsetMinutes = new Date().getTimezoneOffset();

  return {
    timezone,
    locale,
    timezoneOffsetMinutes,
    timezoneSource: timezone ? "iana" : "offset",
  };
}
